import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import { authHeaders } from '../utils/authHeaders';

const API = process.env.REACT_APP_BACKEND_URL || '';
const activeSite = () => localStorage.getItem('current_location_id') || '';
const key = () => window.crypto?.randomUUID?.() || `job-${Date.now()}-${Math.random().toString(36).slice(2)}`;

async function readResponse(response, fallback) {
  const body = await response.json().catch(() => ({}));
  if (!response.ok) throw new Error(body.detail || `${fallback} (${response.status})`);
  return body;
}

export function ContractorJobs() {
  const [siteId, setSiteId] = useState(activeSite);
  const [jobs, setJobs] = useState([]);
  const [links, setLinks] = useState({});
  const [loading, setLoading] = useState(true);
  const [busyId, setBusyId] = useState('');
  const [error, setError] = useState('');
  const createKey = useRef('');
  const reviewKeys = useRef({});
  const loadSequence = useRef(0);

  useEffect(() => {
    const sync = () => setSiteId(activeSite());
    window.addEventListener('locationChanged', sync);
    window.addEventListener('storage', sync);
    return () => {
      window.removeEventListener('locationChanged', sync);
      window.removeEventListener('storage', sync);
    };
  }, []);

  const load = useCallback(async () => {
    const sequence = ++loadSequence.current;
    if (!siteId) { setJobs([]); setLoading(false); return; }
    setLoading(true);
    setError('');
    try {
      const response = await fetch(`${API}/api/contractor/jobs`, { headers: authHeaders({ locationId: siteId }) });
      const body = await readResponse(response, 'Could not load contractor jobs');
      if (sequence === loadSequence.current && siteId === activeSite()) setJobs(body.jobs || []);
    } catch (err) {
      if (sequence === loadSequence.current && siteId === activeSite()) setError(err.message || 'Could not load contractor jobs');
    } finally {
      if (sequence === loadSequence.current) setLoading(false);
    }
  }, [siteId]);

  useEffect(() => { setJobs([]); setLinks({}); load(); }, [load]);

  const create = async (event) => {
    event.preventDefault();
    const formElement = event.currentTarget;
    const form = new FormData(formElement);
    if (!createKey.current) createKey.current = key();
    setBusyId('create');
    setError('');
    try {
      const response = await fetch(`${API}/api/contractor/jobs`, {
        method: 'POST',
        headers: { ...authHeaders({ locationId: siteId }), 'Content-Type': 'application/json' },
        body: JSON.stringify({
          idempotency_key: createKey.current,
          title: form.get('title'),
          description: form.get('description'),
          contractor_name: form.get('contractor_name'),
          contractor_email: form.get('contractor_email'),
          due_date: form.get('due_date') ? new Date(`${form.get('due_date')}T12:00:00`).toISOString() : null,
          required_certificates: String(form.get('required_certificates') || '').split('\n').map((item) => item.trim()).filter(Boolean),
        }),
      });
      await readResponse(response, 'Could not create contractor job');
      createKey.current = '';
      formElement.reset();
      await load();
    } catch (err) {
      setError(err.message || 'Could not create contractor job');
    } finally {
      setBusyId('');
    }
  };

  const issueGrant = async (jobId) => {
    setBusyId(jobId);
    setError('');
    try {
      const response = await fetch(`${API}/api/contractor/jobs/${encodeURIComponent(jobId)}/grant`, {
        method: 'POST',
        headers: { ...authHeaders({ locationId: siteId }), 'Content-Type': 'application/json' },
        body: JSON.stringify({ expires_in_hours: 72 }),
      });
      const body = await readResponse(response, 'Could not issue contractor access');
      const link = `${window.location.origin}/contractor/job/${encodeURIComponent(jobId)}#${body.access_token}`;
      setLinks((current) => ({ ...current, [jobId]: { link, expiresAt: body.expires_at } }));
      setJobs((current) => current.map((job) => job.id === jobId ? { ...job, grant_active: true } : job));
    } catch (err) {
      setError(err.message || 'Could not issue contractor access');
    } finally {
      setBusyId('');
    }
  };

  const revokeGrant = async (jobId) => {
    setBusyId(jobId);
    setError('');
    try {
      const response = await fetch(`${API}/api/contractor/jobs/${encodeURIComponent(jobId)}/grant/revoke`, {
        method: 'POST',
        headers: authHeaders({ locationId: siteId }),
      });
      await readResponse(response, 'Could not revoke contractor access');
      setLinks((current) => { const next = { ...current }; delete next[jobId]; return next; });
      await load();
    } catch (err) {
      setError(err.message || 'Could not revoke contractor access');
    } finally {
      setBusyId('');
    }
  };

  const review = async (job, decision) => {
    const notes = window.prompt(decision === 'ACCEPTED' ? 'Record what you checked before accepting:' : 'Explain the rework required:');
    if (!notes?.trim()) return;
    let verifiedCertificateRequirements = [];
    if (decision === 'ACCEPTED' && job.required_certificates?.length) {
      const labels = window.prompt(
        `After checking the originals, enter every required certificate label below, one per line:\n\n${job.required_certificates.join('\n')}`,
      );
      if (labels === null) return;
      verifiedCertificateRequirements = labels.split('\n').map((item) => item.trim()).filter(Boolean);
      if (verifiedCertificateRequirements.length !== job.required_certificates.length
        || job.required_certificates.some((required) => !verifiedCertificateRequirements.includes(required))) {
        setError('Acceptance requires explicit review of every required certificate. No status was changed.');
        return;
      }
    }
    if (!reviewKeys.current[job.id]) reviewKeys.current[job.id] = key();
    setBusyId(job.id);
    setError('');
    try {
      const response = await fetch(`${API}/api/contractor/jobs/${encodeURIComponent(job.id)}/review`, {
        method: 'POST',
        headers: { ...authHeaders({ locationId: siteId }), 'Content-Type': 'application/json' },
        body: JSON.stringify({
          idempotency_key: reviewKeys.current[job.id],
          decision,
          review_notes: notes.trim(),
          verified_certificate_requirements: verifiedCertificateRequirements,
        }),
      });
      await readResponse(response, 'Could not review contractor submission');
      delete reviewKeys.current[job.id];
      await load();
    } catch (err) {
      setError(err.message || 'Could not review contractor submission');
    } finally {
      setBusyId('');
    }
  };

  return (
    <main className="mx-auto max-w-5xl px-4 py-6" data-testid="contractor-jobs-page">
      <header className="border-b border-gray-200 pb-4">
        <h1 className="text-2xl font-semibold text-gray-900">Contractor jobs</h1>
        <p className="mt-1 text-sm text-gray-600">Site-scoped assignments, submitted evidence and human acceptance.</p>
      </header>
      {!siteId ? <p className="mt-5" role="status">Select an authorized site to continue.</p> : (
        <>
          <form className="mt-5 grid max-w-2xl grid-cols-1 gap-3 sm:grid-cols-2" onSubmit={create}>
            <h2 className="text-lg font-medium sm:col-span-2">Assign a job</h2>
            <label className="text-sm">Job title<input required minLength="3" maxLength="160" name="title" className="mt-1 block h-10 w-full border border-gray-300 px-2" /></label>
            <label className="text-sm">Contractor name<input required maxLength="160" name="contractor_name" className="mt-1 block h-10 w-full border border-gray-300 px-2" /></label>
            <label className="text-sm">Contractor email<input required type="email" maxLength="254" name="contractor_email" className="mt-1 block h-10 w-full border border-gray-300 px-2" /></label>
            <label className="text-sm">Due date<input name="due_date" type="date" className="mt-1 block h-10 w-full border border-gray-300 px-2" /></label>
            <label className="text-sm sm:col-span-2">Work description<textarea required minLength="5" maxLength="4000" name="description" rows="3" className="mt-1 block w-full border border-gray-300 p-2" /></label>
            <label className="text-sm sm:col-span-2">Required certificate references, one per line<textarea maxLength="3200" name="required_certificates" rows="2" className="mt-1 block w-full border border-gray-300 p-2" /></label>
            <button disabled={busyId === 'create'} className="h-10 w-fit border border-gray-500 px-3 text-sm disabled:opacity-50">{busyId === 'create' ? 'Saving…' : 'Create job'}</button>
          </form>
          {error && <p className="mt-3 text-sm text-red-700" role="alert">{error}</p>}
          <section className="mt-8" aria-labelledby="contractor-jobs-list-title">
            <h2 id="contractor-jobs-list-title" className="text-lg font-medium">Site jobs</h2>
            {loading ? <p className="mt-3" role="status">Loading jobs…</p> : jobs.length === 0 ? <p className="mt-3 text-sm text-gray-600">No contractor jobs recorded for this site.</p> : (
              <ul className="mt-3 divide-y divide-gray-200 border-y border-gray-200">
                {jobs.map((job) => (
                  <li key={job.id} className="py-4" data-testid={`contractor-job-${job.id}`}>
                    <div className="flex flex-wrap items-start justify-between gap-3">
                      <div>
                        <h3 className="font-medium">{job.title}</h3>
                        <p className="mt-1 text-sm text-gray-700">{job.description}</p>
                        <p className="mt-1 text-xs text-gray-600">{job.contractor_name} · {job.contractor_email} · {job.status}</p>
                        {job.evidence_reference && <p className="mt-2 text-sm">Evidence reference: {job.evidence_reference}</p>}
                        {job.completion_summary && <p className="mt-1 text-sm">{job.completion_summary}</p>}
                        {job.certificate_references?.length > 0 && <p className="mt-1 text-sm">Certificates: {job.certificate_references.join(', ')}</p>}
                        {job.review_notes && <p className="mt-1 text-sm">Review: {job.review_notes}</p>}
                        {job.verified_certificate_requirements?.length > 0 && <p className="mt-1 text-sm">Required certificates checked by {job.reviewed_by_name || 'site manager'}: {job.verified_certificate_requirements.join(', ')}</p>}
                      </div>
                      <div className="flex flex-wrap gap-2">
                        {['ASSIGNED', 'REWORK_REQUIRED'].includes(job.status) && <button type="button" disabled={busyId === job.id} onClick={() => issueGrant(job.id)} className="h-9 border border-gray-500 px-3 text-sm">{busyId === job.id ? 'Issuing…' : 'Issue / rotate access link'}</button>}
                        {job.grant_active && <button type="button" disabled={busyId === job.id} onClick={() => revokeGrant(job.id)} className="h-9 border border-red-400 px-3 text-sm text-red-700">{busyId === job.id ? 'Revoking…' : 'Revoke access link'}</button>}
                      </div>
                    </div>
                    {links[job.id] && (
                      <div className="mt-3 max-w-2xl border border-gray-200 p-3">
                        <p className="text-sm font-medium">Share this one-time display of the active job link manually.</p>
                        <p className="text-xs text-gray-600">Expires {new Date(links[job.id].expiresAt).toLocaleString()}. Reissuing invalidates the prior link. No message was sent.</p>
                        <input readOnly aria-label={`Contractor link for ${job.title}`} value={links[job.id].link} className="mt-2 h-10 w-full border border-gray-300 px-2 text-xs" />
                        <button type="button" onClick={() => navigator.clipboard?.writeText(links[job.id].link)} className="mt-2 h-9 border border-gray-500 px-3 text-sm">Copy link</button>
                      </div>
                    )}
                    {job.status === 'SUBMITTED' && job.can_review && (
                      <div className="mt-3 flex flex-wrap gap-2">
                        <button type="button" disabled={busyId === job.id} onClick={() => review(job, 'ACCEPTED')} className="h-9 border border-gray-500 px-3 text-sm">Accept checked work</button>
                        <button type="button" disabled={busyId === job.id} onClick={() => review(job, 'REWORK_REQUIRED')} className="h-9 border border-red-400 px-3 text-sm text-red-700">Request rework</button>
                      </div>
                    )}
                    {job.status === 'SUBMITTED' && !job.can_review && <p className="mt-2 text-sm text-amber-800">A different site manager must review this submission.</p>}
                  </li>
                ))}
              </ul>
            )}
          </section>
          <p className="mt-5 text-xs text-gray-600">Acceptance records the manager's attestation that required certificate originals were checked. FoodOps does not authenticate certificates or certify compliance or safety.</p>
        </>
      )}
    </main>
  );
}

export function ContractorJobPortal() {
  const { jobId } = useParams();
  const [token] = useState(() => window.location.hash.slice(1));
  const [job, setJob] = useState(null);
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const submissionKey = useRef('');

  const load = useCallback(async () => {
    if (!token) { setError('This link has no access grant. Ask the site manager for a new link.'); return; }
    try {
      const response = await fetch(`${API}/api/contractor/jobs/${encodeURIComponent(jobId)}/external`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setJob(await readResponse(response, 'Contractor access could not be verified'));
    } catch (err) {
      setError(err.message || 'Contractor access could not be verified');
    }
  }, [jobId, token]);

  useEffect(() => { load(); }, [load]);

  const submit = async (event) => {
    event.preventDefault();
    const form = new FormData(event.currentTarget);
    if (!submissionKey.current) submissionKey.current = key();
    setBusy(true);
    setError('');
    try {
      const response = await fetch(`${API}/api/contractor/jobs/${encodeURIComponent(jobId)}/external/submission`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({
          idempotency_key: submissionKey.current,
          completion_summary: form.get('completion_summary'),
          evidence_reference: form.get('evidence_reference'),
          certificate_references: String(form.get('certificate_references') || '').split('\n').map((item) => item.trim()).filter(Boolean),
        }),
      });
      await readResponse(response, 'Submission failed');
      submissionKey.current = '';
      setSubmitted(true);
      await load();
    } catch (err) {
      setError(err.message || 'Submission failed');
    } finally {
      setBusy(false);
    }
  };

  return (
    <main className="mx-auto max-w-2xl px-4 py-8" data-testid="contractor-job-portal">
      <h1 className="text-2xl font-semibold">Contractor job</h1>
      {error && <p className="mt-4 text-sm text-red-700" role="alert">{error}</p>}
      {!job ? <p className="mt-4" role="status">Verifying job access…</p> : (
        <>
          <section className="mt-5 border-y border-gray-200 py-4">
            <h2 className="text-lg font-medium">{job.title}</h2>
            <p className="mt-2 whitespace-pre-wrap text-sm">{job.description}</p>
            {job.due_date && <p className="mt-2 text-sm">Due: {new Date(job.due_date).toLocaleDateString()}</p>}
            {job.required_certificates.length > 0 && <p className="mt-2 text-sm">Requested certificate references: {job.required_certificates.join(', ')}</p>}
            <p className="mt-2 text-xs text-gray-600">Job status: {job.status}. Provide references only; files are not uploaded through this link.</p>
          </section>
          {['ASSIGNED', 'REWORK_REQUIRED'].includes(job.status) && !submitted && (
            <form className="mt-5 space-y-3" onSubmit={submit}>
              <label className="block text-sm">Completion summary<textarea required minLength="3" maxLength="3000" name="completion_summary" rows="4" className="mt-1 block w-full border border-gray-300 p-2" /></label>
              <label className="block text-sm">Evidence / service record reference<input required maxLength="240" name="evidence_reference" className="mt-1 block h-10 w-full border border-gray-300 px-2" /></label>
              <label className="block text-sm">Certificate references, one per line<textarea maxLength="3200" name="certificate_references" rows="3" className="mt-1 block w-full border border-gray-300 p-2" /></label>
              <button disabled={busy} className="h-10 border border-gray-500 px-3 text-sm disabled:opacity-50">{busy ? 'Submitting…' : 'Submit work for site review'}</button>
            </form>
          )}
          {(submitted || job.status === 'SUBMITTED') && <p className="mt-4" role="status">Submission received. The site manager must review the work and certificate references.</p>}
          {job.status === 'ACCEPTED' && <p className="mt-4" role="status">The site manager recorded acceptance.</p>}
          <p className="mt-6 text-xs text-gray-600">This link is limited to this job and expires. Do not forward it. FoodOps does not verify certificate authenticity or certify safety.</p>
        </>
      )}
    </main>
  );
}
